import React, { useEffect, useState } from 'react'

import {
    Modal,
    Paper,
    Slide,
    Typography,
    IconButton,
    useTheme,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Box,
    Card,
    CardContent,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@material-ui/icons'

import { WorkorderResponse } from '../../../store'
import { DropFiles } from '../../../components/DropFiles/DropFiles'
import { toast } from 'react-toastify'

interface GoBackModalProps {
    open: boolean
    workorder: WorkorderResponse | null
    onClose: () => void
    onSubmit: (workorderId: number, formData: FormData) => void
}

export const GoBackModal = ({
    open,
    workorder,
    onClose,
    onSubmit,
}: GoBackModalProps) => {
    const theme = useTheme()

    const [loading, setLoading] = useState(false)
    const [userDescription, setUserDescription] = useState('')
    const [selectedServiceAreas, setSelectedServiceAreas] = useState<number[]>(
        [],
    )
    const [capturedImages, setCapturedImages] = useState<File[]>([])
    const [imageReplaceId, setImageReplaceId] = useState<number | undefined>()

    useEffect(() => {
        if (workorder) {
            setSelectedServiceAreas(
                workorder.service_area_list?.map((area) => area.area_id) || [],
            )
        }
    }, [workorder])

    const handleServiceAreaToggle = (areaId: number) => {
        setSelectedServiceAreas((prev) => {
            if (prev.includes(areaId)) {
                return prev.filter((id) => id !== areaId)
            } else {
                return [...prev, areaId]
            }
        })
    }

    const handleReplaceImage = (index: number) => {
        setImageReplaceId(index)
    }

    const handleDrop = (files: File[]) => {
        if (files.length === 0) return

        const file = files[0] // Take only the first file

        if (imageReplaceId !== undefined) {
            // Replace existing image
            setCapturedImages((prev) => {
                const newImages = [...prev]
                newImages[imageReplaceId] = file
                return newImages
            })
            setImageReplaceId(undefined)
        } else {
            // Add new image
            if (capturedImages.length < 3) {
                setCapturedImages((prev) => [...prev, file])
            }
        }
    }

    const handleRemoveImage = (index: number) => {
        setCapturedImages((prev) => prev.filter((_, i) => i !== index))
    }

    const handleSubmit = async () => {
        if (!workorder?.id) return

        setLoading(true)
        const formData = new FormData()

        // Add service areas
        selectedServiceAreas.forEach((areaId, index) => {
            formData.append(`area[${index}]`, areaId.toString())
        })

        // Add description
        formData.append('description', userDescription)

        // Add images
        if (capturedImages.length > 0) {
            formData.append('attachments_len', capturedImages.length.toString())
            capturedImages.forEach((image, index) => {
                formData.append(`attachment${index}`, image)
                formData.append(`attachment${index}_uploaded`, 'false')
            })
        }

        try {
            await onSubmit(workorder.id, formData)
            toast.success('Go Back initiated successfully')
            onClose()
        } catch (error) {
            console.error('Error creating go back:', error)
        } finally {
            setLoading(false)
        }
    }

    const renderServiceAreas = () => {
        if (
            !workorder?.service_area_list ||
            workorder.service_area_list.length === 0
        ) {
            return null
        }

        // Sort service areas: occupiable ones first, then alphabetically by name-label
        const sortedServiceAreas = [...workorder.service_area_list].sort(
            (a, b) => {
                // First, sort by occupiable status (occupiable areas first)
                if (a.occupiable && !b.occupiable) return -1
                if (!a.occupiable && b.occupiable) return 1

                // If both have the same occupiable status, sort alphabetically by name-label
                const aLabel = `${a.area_name} - ${a.area_label}`
                const bLabel = `${b.area_name} - ${b.area_label}`
                return aLabel.localeCompare(bLabel)
            },
        )

        return (
            <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                    Service Areas
                </Typography>
                <Grid container spacing={1}>
                    {sortedServiceAreas.map((serviceArea) => (
                        <Grid item xs={12} sm={6} key={serviceArea.area_id}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedServiceAreas.includes(
                                            serviceArea.area_id,
                                        )}
                                        onChange={() =>
                                            handleServiceAreaToggle(
                                                serviceArea.area_id,
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label={`${serviceArea.area_name} - ${serviceArea.area_label}`}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        )
    }

    const renderImageGrid = () => {
        return (
            <Box mb={2}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography variant="subtitle2">
                        Upload Photos (max 3)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {capturedImages.length}/3
                    </Typography>
                </Box>

                {/* DropFiles component for adding new images */}
                {imageReplaceId === undefined && capturedImages.length < 3 && (
                    <Box mb={2}>
                        <DropFiles
                            displayString="Drop an image here or click to browse"
                            fileTypes={{ 'image/*': ['.jpeg', '.jpg', '.png'] }}
                            multipleFiles={false}
                            onDrop={handleDrop}
                        />
                    </Box>
                )}

                {/* DropFiles component for replacing images */}
                {imageReplaceId !== undefined && (
                    <Box mb={2}>
                        <DropFiles
                            displayString={`Drop a new image to replace image ${
                                imageReplaceId + 1
                            }`}
                            fileTypes={{ 'image/*': ['.jpeg', '.jpg', '.png'] }}
                            multipleFiles={false}
                            onDrop={handleDrop}
                        />
                        <Box mt={1} display="flex" justifyContent="center">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setImageReplaceId(undefined)}
                            >
                                Cancel Replace
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Image previews */}
                {capturedImages.length > 0 && (
                    <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Uploaded Images:
                        </Typography>
                        <Grid container spacing={2}>
                            {capturedImages.map((image, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card style={{ position: 'relative' }}>
                                        <CardContent style={{ padding: 0 }}>
                                            <img
                                                src={URL.createObjectURL(image)}
                                                alt={`Uploaded image ${
                                                    index + 1
                                                }`}
                                                style={{
                                                    width: '100%',
                                                    height: 120,
                                                    objectFit: 'cover',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <Box
                                                style={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    style={{
                                                        backgroundColor:
                                                            'rgba(0, 0, 0, 0.6)',
                                                        color: 'white',
                                                    }}
                                                    onClick={() =>
                                                        handleRemoveImage(index)
                                                    }
                                                >
                                                    <DeleteIcon
                                                        style={{ fontSize: 16 }}
                                                    />
                                                </IconButton>
                                            </Box>
                                            <Box
                                                style={{
                                                    position: 'absolute',
                                                    bottom: 8,
                                                    left: 8,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    style={{
                                                        backgroundColor:
                                                            'rgba(0, 0, 0, 0.6)',
                                                        color: 'white',
                                                    }}
                                                    onClick={() =>
                                                        handleReplaceImage(
                                                            index,
                                                        )
                                                    }
                                                >
                                                    <EditIcon
                                                        style={{ fontSize: 16 }}
                                                    />
                                                </IconButton>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Helper text */}
                {capturedImages.length === 0 && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ fontStyle: 'italic' }}
                    >
                        Drop an image above to get started
                    </Typography>
                )}
                {capturedImages.length > 0 && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ fontStyle: 'italic' }}
                    >
                        Click the edit button on images to replace them, or use
                        the X button to remove
                    </Typography>
                )}
            </Box>
        )
    }

    if (!workorder) return null

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    elevation={24}
                    style={{
                        width: '90vw',
                        maxWidth: '800px',
                        maxHeight: '90vh',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Header - Fixed */}
                    <Box
                        p={3}
                        style={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Typography
                                variant="h5"
                                style={{ fontWeight: 600 }}
                            >
                                Create Go Back
                            </Typography>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Content - Scrollable */}
                    <Box
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '24px',
                        }}
                    >
                        {renderImageGrid()}
                        {renderServiceAreas()}

                        <Box mb={3}>
                            <Typography variant="subtitle2" gutterBottom>
                                Description
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                value={userDescription}
                                onChange={(e) =>
                                    setUserDescription(e.target.value)
                                }
                                placeholder="Enter description here"
                            />
                        </Box>
                    </Box>

                    {/* Submit Button - Fixed */}
                    <Box
                        p={3}
                        style={{
                            borderTop: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                                style={{
                                    backgroundColor: theme.palette.primary.dark,
                                    color: 'white',
                                    padding: '12px 24px',
                                    borderRadius: 4,
                                }}
                            >
                                {loading ? 'Loading...' : 'Initiate Go Back'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Slide>
        </Modal>
    )
}

export default GoBackModal
